import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Handshake, Building, Award, TrendingUp, Calendar, FileText,
  CheckCircle, Clock, Star, ChevronRight, LogOut, BarChart3,
  Globe, Briefcase, Shield, Users, DollarSign, Target, Home,
  MessageSquare, Settings, Bell, Menu, X, PieChart, Send,
  Plus, Search, Filter, ArrowRight, RefreshCw, Eye, Download,
  ArrowUpRight, ArrowDownRight, Percent
} from 'lucide-react';
import CNESSLogo from '../components/CNESSLogo';

const PartnerDashboard = () => {
  const [partnerData, setPartnerData] = useState(null);
  const [stats, setStats] = useState(null);
  const [collaborations, setCollaborations] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [messages, setMessages] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeModule, setActiveModule] = useState('dashboard');
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [profileRes, statsRes, collabRes, oppRes, proposalsRes, analyticsRes, messagesRes, orgsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/partner/profile/${userData.id}`),
        fetch(`${BACKEND_URL}/api/partner/dashboard-stats/${userData.id}`),
        fetch(`${BACKEND_URL}/api/partner/collaborations/${userData.id}`),
        fetch(`${BACKEND_URL}/api/partner/opportunities/${userData.id}`),
        fetch(`${BACKEND_URL}/api/partner/proposals/${userData.id}`),
        fetch(`${BACKEND_URL}/api/partner/analytics/${userData.id}`),
        fetch(`${BACKEND_URL}/api/partner/messages/${userData.id}`),
        fetch(`${BACKEND_URL}/api/partner/organizations`)
      ]);

      if (profileRes.ok) setPartnerData(await profileRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (collabRes.ok) setCollaborations(await collabRes.json());
      if (oppRes.ok) setOpportunities(await oppRes.json());
      if (proposalsRes.ok) setProposals(await proposalsRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (messagesRes.ok) setMessages(await messagesRes.json());
      if (orgsRes.ok) setOrganizations(await orgsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'collaborations', label: 'Collaborations', icon: Handshake },
    { id: 'opportunities', label: 'Opportunities', icon: Target },
    { id: 'proposals', label: 'Proposals', icon: FileText },
    { id: 'organizations', label: 'Organizations', icon: Building },
    { id: 'analytics', label: 'Analytics', icon: PieChart },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: messages.filter(m => !m.read).length },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const statsCards = stats ? [
    { label: 'Active Collaborations', value: stats.active_collaborations, icon: Handshake, color: 'bg-blue-500', bgColor: 'bg-blue-50', trend: '+3' },
    { label: 'Organizations Served', value: stats.organizations_served, icon: Building, color: 'bg-green-500', bgColor: 'bg-green-50', trend: '+5' },
    { label: 'Revenue YTD', value: formatCurrency(stats.revenue_ytd), icon: DollarSign, color: 'bg-purple-500', bgColor: 'bg-purple-50', trend: '+23%' },
    { label: 'Partner Rating', value: stats.avg_rating, icon: Star, color: 'bg-amber-500', bgColor: 'bg-amber-50', trend: '+0.1' }
  ] : [];

  const filteredCollaborations = collaborations.filter(c => {
    const matchesSearch = c.organization_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading partner dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-blue-800 via-blue-900 to-indigo-900 text-white transition-all duration-300 flex flex-col fixed h-full z-50`}>
        <div className="p-4 border-b border-blue-700/50">
          <div className="flex items-center justify-between">
            {sidebarOpen ? (
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg p-1.5">
                  <CNESSLogo variant="mark" />
                </div>
                <div>
                  <h1 className="font-bold text-lg">CNESS</h1>
                  <p className="text-xs text-blue-300">Partner Portal</p>
                </div>
              </div>
            ) : (
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mx-auto shadow-lg p-1">
                <CNESSLogo variant="mark" className="!h-7" />
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {sidebarOpen && (
          <div className="p-4 border-b border-blue-700/50">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">{(partnerData?.username || 'P')[0].toUpperCase()}</span>
              </div>
              <div>
                <p className="font-semibold">{partnerData?.username || partnerData?.contact_name || 'Partner'}</p>
                <div className="mt-1">
                  <span className="text-xs text-blue-300">Certified Partner</span>
                  <p className="text-sm font-semibold text-amber-400">{partnerData?.partnership_type || 'Partner'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveModule(item.id)}
                className={`w-full flex items-center ${sidebarOpen ? 'px-4' : 'justify-center'} py-3 rounded-xl transition-all duration-200 ${
                  activeModule === item.id
                    ? 'bg-white/20 text-white shadow-lg'
                    : 'text-blue-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                {sidebarOpen && (
                  <>
                    <span className="ml-3 font-medium">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-blue-700/50">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center ${sidebarOpen ? 'px-4' : 'justify-center'} py-3 rounded-xl text-blue-200 hover:bg-red-500/20 hover:text-red-300 transition-all`}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3 font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <header className="bg-white shadow-sm border-b border-gray-200 px-8 py-4 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {navItems.find(i => i.id === activeModule)?.label || 'Dashboard'}
              </h1>
              <p className="text-sm text-gray-500">Welcome back, {partnerData?.username || 'Partner'}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={fetchAllData}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                title="Refresh data"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 relative transition-colors">
                <Bell className="w-5 h-5 text-gray-600" />
                {messages.filter(m => !m.read).length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                    {messages.filter(m => !m.read).length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        <div className="p-8">
          {/* Dashboard Module */}
          {activeModule === 'dashboard' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {statsCards.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div key={index} className={`${stat.bgColor} rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className={`w-14 h-14 ${stat.color} rounded-xl flex items-center justify-center shadow-lg`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <span className="text-green-600 text-sm font-semibold bg-green-100 px-2 py-1 rounded-full flex items-center">
                          <ArrowUpRight className="w-3 h-3 mr-1" />
                          {stat.trend}
                        </span>
                      </div>
                      <p className="text-3xl font-black text-gray-900">{stat.value}</p>
                      <p className="text-gray-600 font-medium mt-1">{stat.label}</p>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                      <Handshake className="w-6 h-6 mr-2 text-blue-600" />
                      Active Collaborations
                    </h2>
                    <button 
                      onClick={() => setActiveModule('collaborations')}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center"
                    >
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    {collaborations.filter(c => c.status === 'active').slice(0, 4).map((collab, index) => (
                      <div key={index} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900">{collab.organization_name}</h3>
                            <p className="text-sm text-gray-500">{collab.service}</p>
                          </div>
                          <div className="text-right">
                            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-semibold">
                              Active
                            </span>
                            <p className="text-lg font-bold text-gray-900 mt-1">{formatCurrency(collab.value)}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full" 
                              style={{ width: `${collab.progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-gray-700">{collab.progress}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center mb-6">
                    <Target className="w-6 h-6 mr-2 text-blue-600" />
                    New Opportunities
                  </h2>
                  <div className="space-y-4">
                    {opportunities.slice(0, 3).map((opp) => (
                      <div 
                        key={opp.id} 
                        className="border border-gray-200 rounded-xl p-4 hover:border-green-300 transition-all cursor-pointer"
                        onClick={() => {
                          setSelectedOpportunity(opp);
                          setShowProposalModal(true);
                        }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 text-sm">{opp.organization_name}</h4>
                          <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                            opp.urgency === 'high' ? 'bg-red-100 text-red-700' :
                            opp.urgency === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {opp.urgency}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{opp.service_type}</p>
                        <p className="text-lg font-bold text-green-600">{formatCurrency(opp.estimated_value)}</p>
                      </div>
                    ))}
                  </div>
                  <button 
                    onClick={() => setActiveModule('opportunities')}
                    className="w-full mt-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center"
                  >
                    View All Opportunities
                  </button>
                </div>
              </div>

              {/* Quick Analytics */}
              {analytics && (
                <div className="mt-8 bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Revenue Overview</h2>
                  <div className="grid grid-cols-6 gap-4">
                    {analytics.monthly_revenue?.map((month, index) => (
                      <div key={index} className="text-center">
                        <div className="relative h-32 bg-gray-100 rounded-lg overflow-hidden">
                          <div 
                            className="absolute bottom-0 w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-lg transition-all duration-500"
                            style={{ height: `${(month.revenue / 60000) * 100}%` }}
                          />
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mt-2">{formatCurrency(month.revenue)}</p>
                        <p className="text-xs text-gray-500">{month.month}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Collaborations Module */}
          {activeModule === 'collaborations' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">My Collaborations</h2>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search collaborations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Organization</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Service</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-600">Progress</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-600">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCollaborations.map((collab, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-semibold text-gray-900">{collab.organization_name}</p>
                            <p className="text-sm text-gray-500">{collab.industry}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-600">{collab.service}</td>
                        <td className="py-4 px-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            collab.status === 'active' ? 'bg-green-100 text-green-700' :
                            collab.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {collab.status}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${collab.progress}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-gray-600">{collab.progress}%</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right font-bold text-gray-900">{formatCurrency(collab.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Opportunities Module */}
          {activeModule === 'opportunities' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Available Opportunities</h2>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-500">{opportunities.length} opportunities</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {opportunities.map((opp) => (
                  <div key={opp.id} className="border border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-lg transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                        {opp.organization_name?.charAt(0)}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        opp.urgency === 'high' ? 'bg-red-100 text-red-700' :
                        opp.urgency === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {opp.urgency} priority
                      </span>
                    </div>
                    
                    <h3 className="font-bold text-gray-900 mb-1">{opp.organization_name}</h3>
                    <p className="text-sm text-gray-500 mb-3">{opp.industry} • {opp.certification_level}</p>
                    
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="text-sm font-semibold text-gray-700">{opp.service_type}</p>
                      <p className="text-xs text-gray-500 mt-1">{opp.description}</p>
                    </div>
                    
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-black text-green-600">{formatCurrency(opp.estimated_value)}</span>
                      <span className="text-sm text-gray-500">Est. Value</span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span>Posted: {opp.posted_date}</span>
                      <span>Deadline: {opp.deadline}</span>
                    </div>
                    
                    <button 
                      onClick={() => {
                        setSelectedOpportunity(opp);
                        setShowProposalModal(true);
                      }}
                      className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                    >
                      Submit Proposal
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Proposals Module */}
          {activeModule === 'proposals' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">My Proposals</h2>
                <div className="flex items-center space-x-4">
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                    {proposals.length} Total
                  </span>
                </div>
              </div>
              
              <div className="space-y-4">
                {proposals.map((proposal) => (
                  <div key={proposal.id} className="border border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-gray-900">{proposal.organization_name}</h3>
                        <p className="text-sm text-gray-500">{proposal.service_type}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        proposal.status === 'accepted' ? 'bg-green-100 text-green-700' :
                        proposal.status === 'under_review' ? 'bg-yellow-100 text-yellow-700' :
                        proposal.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {proposal.status?.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 mb-4">{proposal.description}</p>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Proposed Value</p>
                        <p className="font-bold text-gray-900">{formatCurrency(proposal.proposed_value)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Timeline</p>
                        <p className="font-bold text-gray-900">{proposal.timeline}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Submitted</p>
                        <p className="font-bold text-gray-900">{new Date(proposal.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {proposal.deliverables?.map((d, i) => (
                        <span key={i} className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                          {d}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Organizations Module */}
          {activeModule === 'organizations' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">CNESS Network Organizations</h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {organizations.map((org) => (
                  <div key={org.id} className="border border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-lg transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                        {org.name?.charAt(0)}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        org.certification_level === 'Luminary' ? 'bg-purple-100 text-purple-700' :
                        org.certification_level === 'Inspired' ? 'bg-green-100 text-green-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {org.certification_level}
                      </span>
                    </div>
                    
                    <h3 className="font-bold text-gray-900 mb-1">{org.name}</h3>
                    <p className="text-sm text-gray-500 mb-4">{org.industry}</p>
                    
                    <div className="space-y-2 text-sm">
                      {org.contact_email && (
                        <div className="flex items-center text-gray-600">
                          <MessageSquare className="w-4 h-4 mr-2 text-gray-400" />
                          {org.contact_email}
                        </div>
                      )}
                      {org.contact_name && (
                        <div className="flex items-center text-gray-600">
                          <Users className="w-4 h-4 mr-2 text-gray-400" />
                          {org.contact_name}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analytics Module */}
          {activeModule === 'analytics' && analytics && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Total Revenue YTD</h3>
                  <p className="text-4xl font-black text-blue-600">{formatCurrency(analytics.total_revenue_ytd)}</p>
                  <div className="flex items-center mt-2 text-green-600">
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                    <span className="font-semibold">{analytics.growth_rate}% growth</span>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Client Metrics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Clients</span>
                      <span className="font-bold">{analytics.client_metrics?.total_clients}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Repeat Clients</span>
                      <span className="font-bold">{analytics.client_metrics?.repeat_clients}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Retention Rate</span>
                      <span className="font-bold text-green-600">{analytics.client_metrics?.retention_rate}%</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="font-bold text-gray-900 mb-4">Project Stats</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Project Value</span>
                      <span className="font-bold">{formatCurrency(analytics.client_metrics?.avg_project_value)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Duration</span>
                      <span className="font-bold">{analytics.client_metrics?.avg_project_duration}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-6">Monthly Revenue</h3>
                <div className="grid grid-cols-6 gap-4">
                  {analytics.monthly_revenue?.map((month, index) => (
                    <div key={index} className="text-center">
                      <div className="relative h-40 bg-gray-100 rounded-lg overflow-hidden">
                        <div 
                          className="absolute bottom-0 w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-lg transition-all duration-500"
                          style={{ height: `${(month.revenue / 60000) * 100}%` }}
                        />
                      </div>
                      <p className="text-sm font-semibold text-gray-900 mt-2">{formatCurrency(month.revenue)}</p>
                      <p className="text-xs text-gray-500">{month.month}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-6">Service Distribution</h3>
                <div className="space-y-4">
                  {analytics.service_distribution?.map((service, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-1">
                        <span className="text-gray-700 font-medium">{service.service}</span>
                        <span className="text-gray-600">{service.count} projects ({service.percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full"
                          style={{ width: `${service.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Messages Module */}
          {activeModule === 'messages' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">Inbox</h3>
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-semibold">
                    {messages.filter(m => !m.read).length} unread
                  </span>
                </div>
                <div className="space-y-2">
                  {messages.map((msg) => (
                    <div 
                      key={msg.id}
                      className={`p-3 rounded-xl cursor-pointer transition-all ${
                        !msg.read ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-900 text-sm">{msg.sender_name}</span>
                        {!msg.read && <span className="w-2 h-2 bg-blue-500 rounded-full"></span>}
                      </div>
                      <p className="text-sm text-gray-600 font-medium">{msg.subject}</p>
                      <p className="text-xs text-gray-400 mt-1 truncate">{msg.content}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-6">Compose Message</h3>
                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                      <option>Select organization...</option>
                      {organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter subject..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea 
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Type your message..."
                    />
                  </div>
                  <button 
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Settings Module */}
          {activeModule === 'settings' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Settings</h2>
              <div className="max-w-2xl space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Profile Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                      <input 
                        type="text" 
                        value={partnerData?.username || ''}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input 
                        type="email" 
                        value={partnerData?.email || ''}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Notification Preferences</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                      <span className="ml-3 text-gray-700">Email notifications for new opportunities</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                      <span className="ml-3 text-gray-700">Proposal status updates</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" />
                      <span className="ml-3 text-gray-700">Weekly analytics reports</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Proposal Modal */}
      {showProposalModal && selectedOpportunity && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Submit Proposal</h3>
            <p className="text-gray-600 mb-4">For {selectedOpportunity.organization_name} - {selectedOpportunity.service_type}</p>
            
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proposed Value</label>
                <input 
                  type="number" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={selectedOpportunity.estimated_value}
                  defaultValue={selectedOpportunity.estimated_value}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timeline</label>
                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option>1-2 months</option>
                  <option>2-3 months</option>
                  <option>3-4 months</option>
                  <option>4-6 months</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proposal Description</label>
                <textarea 
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe your approach and value proposition..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key Deliverables</label>
                <textarea 
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="List main deliverables (one per line)..."
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button 
                  type="button"
                  onClick={() => {
                    setShowProposalModal(false);
                    setSelectedOpportunity(null);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg"
                >
                  Submit Proposal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerDashboard;
